import React, { useRef, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Header from '../shared/header/header';
import Footer from '../shared/footer/footer';
import useGetdata from '../../Hooks/useGetData';
import "./refundPolicy.css";
import SnackbarNav from '../main/SnackbarNav/SnackbarNav';
import MobileMenu from '../main/SnackbarNav/MobileMenu';

const refundSections = [
    {
        id: "introduction",
        title: "Introduction",
        type: "text-box",
        content: `This Refund and Cancellation Policy (“Policy”) governs all payments made through the
website of Skye Air Mobility Private Limited (hereinafter referred to as the “Company”)
using integrated payment gateway or payment aggregator services. By accessing the Website
and making any payment therein, the user (hereinafter referred to as the “Customer”)
expressly agrees to be bound by the terms and conditions of this Policy.`,
    },
    {
        id: "nature-service",
        title: "Nature of Services",
        type: "text",
        content: `The Company operates as a service provider engaged in logistics and related services and
facilitates payments through authorised payment aggregators and payment gateways
regulated by the Reserve Bank of India. It is expressly clarified that the Company does not
store or retain any sensitive customer payment credentials, including but not limited to card
details, CVV, OTP, or UPI PIN, and ensures compliance with all applicable regulatory norms
and guidelines prescribed by the Reserve Bank of India.`
    },
    {
        id: "cancellation",
        title: "Cancellation Policy",
        type: "text",
        content: [
            `A Customer may initiate cancellation of a transaction or order prior to the commencement or execution of the relevant service, subject to any specific cancellation window that may be
prescribed for such service on the Website. Cancellation may also be permitted in cases
where a duplicate payment has been made by the Customer.
`,
            `The Company reserves the absolute right to cancel any transaction or order at its sole
discretion in circumstances including, but not limited to, operational or technical errors, non-
availability of services, suspected fraudulent or suspicious transactions, or where such
cancellation is required to ensure compliance with applicable laws or regulatory
requirements.`
        ]
    },
    {
        id: "refund",
        title: "Refund Policy",
        type: "text-multi",
        paragraphs: [
            `Refunds shall be issued only in bona fide cases, including where a transaction has failed but
the amount has been debited from the Customer’s account without corresponding service
delivery, where duplicate payments have been made, where a valid cancellation request has
been accepted within the permissible period, where the Company is unable to fulfil the
service, or where any billing error has occurred.
`,
            `All refunds shall be processed strictly to the original mode of payment used at the time of
transaction, unless otherwise agreed in writing, and under no circumstances shall cash
refunds be provided.`,
            `In the case of failed transactions, refunds are typically processed as auto-reversals within a
T+1 working day timeline in accordance with the regulatory framework prescribed by the
`,
            `Reserve Bank of India. For standard refund requests, the Company shall initiate the refund
process within two to five (2–5) working days from the date of approval. The actual credit of
the refunded amount to the Customer’s account shall be subject to banking channels and may
take approximately one to five (1–5) working days in case of UPI transactions and three to
seven (3–7) working days in case of card or net banking transactions.
`,
            `In the event that the refund is not processed within the timelines stated above, the Customer
may first contact the Company’s customer support. Thereafter, the Customer may escalate the
matter to the respective payment gateway or issuing bank, and if required, initiate a complaint
through the grievance redressal or Online Dispute Resolution (ODR) mechanism prescribed
by the Reserve Bank of India`
        ]
    },

    {
        id: "nonrefund",
        title: "Non-Refundable Situations",
        type: "text-multi",
        paragraphs: [
            `Refunds shall not be entertained in circumstances where the service has already been
rendered or substantially performed, where incorrect or incomplete information has been
provided by the Customer, where the Customer is found to be in violation of the applicable
terms of use of the Website, or where delays or failures are attributable to the Customer or
circumstances beyond the control of the Company.
        `]
    },
    {
        id: "disputes",
        title: "Chargebacks and Disputes",
        type: "text-multi",
        paragraphs: [
            `The Customer may initiate a chargeback or dispute through their issuing bank or payment
service provider in accordance with applicable rules. The Company reserves the right to
contest any such chargeback or dispute by providing relevant transaction records and
evidence. In cases involving excessive, repetitive, or fraudulent chargebacks, the Company
may take appropriate action, including suspension or restriction of access to the Website or
services.
        `,
            `
The Company maintains a structured mechanism for handling disputes and tracking
complaints in compliance with regulatory requirements prescribed by the Reserve Bank of
India.`]
    },
    {
        id: "redressal",
        title: "Grievance Redressal Mechanism",
        type: "text-multi",
        paragraphs: [
            `For any concerns or disputes relating to refunds or cancellations, the Customer may contact
the designated Grievance Officer of the Company. The details of the Grievance Officer shall
be made available on the Website.
        `,
            `All complaints shall be acknowledged within a period of twenty-four (24) hours from receipt,
and the Company shall endeavour to resolve such complaints within five (5) working days, in
accordance with applicable regulatory standards.`]
    },
    {
        id: "disclosures",
        title: "Important Disclosures",
        type: "text-multi",
        paragraphs: [
            `The Customer acknowledges and agrees that the Company is solely responsible for the
provision of services, handling of refunds, and resolution of disputes. Payment aggregators
and gateways act only as intermediaries facilitating the processing of payments and shall not
be held liable for service-related issues.
        `,
            `
The Customer is advised to carefully review all applicable policies, including the Terms of
Use and Privacy Policy, prior to making any payment on the Website. The Company ensures
that all such policies are displayed in accordance with applicable regulatory requirements.`]
    },
    {
        id: "liability",
        title: "Limitation of Liability",
        type: "text-multi",
        paragraphs: [
            `The Company shall not be held liable for any delay or failure in processing refunds arising
due to delays in banking systems, payment networks, or intermediary institutions, nor for any
technical failures attributable to third-party payment gateways or service providers. The
Company shall also not be liable for any failure or delay caused due to force majeure events
beyond its reasonable control.`]
    },
    {
        id: "policyUpdates",
        title: "Policy Updates",
        type: "text-multi",
        paragraphs: [
            `The Company reserves the right to amend, modify, or update this Policy at any time without
prior notice. Any such changes shall be effective immediately upon publication on the
Website, and continued use of the Website by the Customer shall constitute acceptance of the
revised Policy.`]
    },
];

const RefundPolicy = () => {
    const sectionRef = useRef(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 480);
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 480);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useGetdata();
    const data = useSelector((store) => store.data.HomeData);

    useEffect(() => {
        const handleScroll = () => {
            const links = document.querySelectorAll(".nav-link");
            refundSections.forEach((sec) => {
                const el = document.getElementById(sec.id);
                if (!el) return;
                const rect = el.getBoundingClientRect();
                if (rect.top <= 150 && rect.bottom >= 150) {
                    links.forEach((l) => l.classList.remove("active"));
                    document.querySelector(`[data-link="${sec.id}"]`)?.classList.add("active");
                }
            });
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    return (
        <div className="terms-page-wrapper">
            <div ref={sectionRef} className="contact-container">
                <div className="terms-home-container">
                    <Header />
                </div>
            </div>

            <div className="terms-page">
                <div className="terms-hero">
                    <div className="overlay">
                        <p className="breadcrumb">LEGAL / POLICIES</p>
                        <h1>Refund & Cancellation Policy</h1>
                        <p className="subtitle">
                            Our commitment to customer satisfaction and fair refund & Cancellation process.
                        </p>
                        <p className="last-updated">Last Updated: March 26, 2026</p>
                    </div>
                </div>

                <div className="terms-body">
                    {!isMobile && (
                        <div className="terms-sidebar">
                            <p className="sidebar-title">QUICK NAVIGATION</p>
                            {refundSections.map((item) => (
                                <a
                                    key={item.id}
                                    href={`#${item.id}`}
                                    data-link={item.id}
                                    className="nav-link"
                                >
                                    {item.title}
                                </a>
                            ))}
                        </div>
                    )}

                    <div className="terms-content">
                        {refundSections.map((sec) => (
                            <section key={sec.id} id={sec.id}>
                                <h2>{sec.title}</h2>
                                <hr className="section-divider" />

                                {sec.type === "text" && <p>{sec.content}</p>}

                                {sec.type === "text-box" && (
                                    <>
                                        <p>{sec.content}</p>
                                        {/* <div className="policy-note-box">
                                            <p>{sec.boxContent}</p>
                                        </div> */}
                                    </>
                                )}

                                {sec.type === "list" && (
                                    <>
                                        <p>{sec.content}</p>
                                        {sec.subsections.map((sub, i) => (
                                            <div key={i} className="policy-subsection">
                                                <h6 style={{ color: '#fff' }}>{sub.subtitle}</h6>
                                                <ul>
                                                    {sub.points.map((p, j) => <li key={j}>{p}</li>)}
                                                </ul>
                                            </div>
                                        ))}
                                    </>
                                )}

                                {sec.type === "text-multi" && sec.paragraphs.map((p, i) => <p key={i}>{p}</p>)}

                                {sec.type === "steps" && (
                                    <div className="steps-container">
                                        <p>{sec.content}</p>
                                        {sec.steps.map((step) => (
                                            <div key={step.id} className="step-item">
                                                <div className="step-number">{step.id}</div>
                                                <div className="step-text">
                                                    <h4>{step.title}</h4>
                                                    <p>{step.desc}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {sec.type === "list-bold" && (
                                    <>
                                        <p>{sec.content}</p>
                                        <ul>
                                            {sec.points.map((p, i) => (
                                                <li key={i}><strong>{p.label}:</strong> {p.desc}</li>
                                            ))}
                                        </ul>
                                    </>
                                )}

                                {sec.type === "contact-box" && (
                                    <>
                                        <p>{sec.content}</p>
                                        <div className="policy-contact-card">
                                            <div className="contact-field">
                                                <label>Email</label>
                                                <p className="blue-link">{sec.email}</p>
                                            </div>
                                            <div className="contact-field">
                                                <label>Phone</label>
                                                <p>{sec.phone}</p>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </section>
                        ))}
                    </div>
                </div>
            </div>
            <Footer />

            <section>
                {!isMobile && <SnackbarNav />}
                {isMobile && !menuOpen && (
                    <button className="menu-toggle" onClick={() => setMenuOpen(true)}>
                        ☰
                    </button>
                )}
                {isMobile && menuOpen && (
                    <MobileMenu isOpen={menuOpen} onClose={() => setMenuOpen(false)} />
                )}
            </section>
        </div>
    );
};

export default RefundPolicy;